// src/plugins/decimalTextFieldPlugin.js
import { h, defineComponent, ref, watch, getCurrentInstance } from "vue"
import { VTextField } from "vuetify/components"

// --- helpers ---
const toDisplay = (v) => (v == null || v === "") ? "" : String(v).replace(".", ",")
const toDot = (s) => String(s ?? "").replace(",", ".")
function sanitize(raw, allowNeg) {
    let s = String(raw ?? "").replace(/\s/g, "").replace(/\./g, ",")
    const parts = s.split(",")
    if (parts.length > 2) s = parts[0] + "," + parts.slice(1).join("").replace(/,/g, "")
    s = s.replace(/(?!^-)[^0-9,]/g, "")
    if (!allowNeg) s = s.replace(/-/g, "")
    else {
        s = s.replace(/-/g, "")
        if (String(raw).trim().startsWith("-")) s = "-" + s
    }
    return s
}
function formatSv(n) {
    try { return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 20 }).format(n) }
    catch (e) { return String(n) }
}
const normRulesVal = (v) => (v == null ? v : String(v).replace(",", "."))
const wrapRules = (rules) =>
    !rules ? rules : (Array.isArray(rules) ? rules : [rules]).map(r =>
        typeof r === "function" ? (val) => r(normRulesVal(val)) : r)

// --- plugin ---
export default {
    install(app) {
        const Patched = defineComponent({
            name: "VTextField",
            inheritAttrs: false,
            props: {
                modelValue: { type: [String, Number], default: undefined }, // v-model från parent
                decimalSv: { type: Boolean, default: false },              // OPT-IN (kräver v-model)
                returnNumber: { type: Boolean, default: false },
                formatOnBlur: { type: Boolean, default: false },
                allowNegative: { type: Boolean, default: false },
            },
            emits: ["update:modelValue", "blur", "change", "input"],
            setup(props, { attrs, slots, emit }) {
                // Detektera om parent faktiskt satt v-model (utan att göra fältet controlled i onödan)
                const inst = getCurrentInstance()
                const vnodeProps = (inst && inst.vnode && inst.vnode.props) || {}
                const hasParentModel =
                    ("modelValue" in vnodeProps) || ("model-value" in vnodeProps) || ("onUpdate:modelValue" in vnodeProps)

                // UI-state (visar alltid med komma) — används ENDAST i opt-in + v-model
                const ui = ref(toDisplay(props.modelValue))
                watch(() => props.modelValue, (nv) => {
                    const next = toDisplay(nv)
                    if (next !== ui.value) ui.value = next
                })

                const call = (k, ...a) => { const fn = attrs && attrs[k]; if (typeof fn === "function") fn(...a) }

                function onUpdateDecimal(val) {
                    const clean = sanitize(val, props.allowNegative)
                    if (clean !== ui.value) ui.value = clean
                    const norm = toDot(clean)
                    const toEmit =
                        (norm === "" || norm === "-" || norm === "-." || norm === ".")
                            ? null
                            : (props.returnNumber ? Number(norm) : norm)
                    emit("update:modelValue", toEmit)
                    emit("input", clean)
                    call("onUpdate:modelValue", toEmit)
                }
                function onUpdatePassthrough(val) {
                    emit("update:modelValue", val)
                    emit("input", val)
                    call("onUpdate:modelValue", val)
                }
                function onBlur(ev) {
                    if (props.decimalSv && hasParentModel && props.formatOnBlur && ui.value && ui.value !== "-" && ui.value !== ",") {
                        const n = Number(toDot(ui.value))
                        if (!Number.isNaN(n)) ui.value = formatSv(n)
                    }
                    emit("blur", ev); call("onBlur", ev)
                }
                function onChange(e) { emit("change", e); call("onChange", e) }

                return () => {
                    // 🧠 Viktigt: gör INGET “controlled” om parent saknar v-model
                    if (!(hasParentModel)) {
                        // Helt pass-through — rör ingenting (så vanliga textboxar funkar som innan)
                        return h(VTextField, { ...attrs }, slots)
                    }

                    // Parent har v-model:
                    if (props.decimalSv) {
                        // ✅ Opt-in: vi visar med komma och emitterar normaliserat till parent
                        const pass = {
                            ...attrs,
                            // tipsar mobil-tangentbord, men rör inte typ i andra fall
                            type: attrs.type ?? "text",
                            inputmode: attrs.inputmode ?? "decimal",
                            modelValue: ui.value,
                            "onUpdate:modelValue": onUpdateDecimal,
                            onBlur, onChange,
                        }
                        if (attrs.rules) pass.rules = wrapRules(attrs.rules) // rules får "."-värde
                        return h(VTextField, pass, slots)
                    }

                    // ✅ v-model men ingen decimal: ren pass-through, inget påverkas
                    return h(VTextField, {
                        ...attrs,
                        modelValue: props.modelValue,
                        "onUpdate:modelValue": onUpdatePassthrough,
                        onBlur, onChange,
                    }, slots)
                }
            },
        })

        app.component("VTextField", Patched)
    }
}
